import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { downloadBlob } from "@/components/DataTable";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, ChevronRight, CheckCircle2, PackageCheck, Rocket, Cylinder,
  ExternalLink, FileDown, PauseCircle, PlayCircle, XCircle, CheckSquare, Truck,
} from "lucide-react";
import LineItemsEditor from "@/components/LineItemsEditor";
import AuditTrail from "@/components/AuditTrail";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";
import ConvertOaToSupplierPoDialog from "@/components/ConvertOaToSupplierPoDialog";
import CommercialChain from "@/components/CommercialChain";
import SendEmailDialog from "@/components/SendEmailDialog";
import { Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const CONFIRM_ROLES = new Set(["plant_head", "admin", "super_admin"]);
const EMAIL_ROLES = new Set(["admin", "super_admin", "plant_head", "sales"]);

export default function OaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [releaseDialog, setReleaseDialog] = useState(false);
  const [holdDialog, setHoldDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [supplierPoDialog, setSupplierPoDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [supplierPos, setSupplierPos] = useState([]);
  const [dialogReason, setDialogReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [linkedCylinders, setLinkedCylinders] = useState([]);

  async function refresh() {
    setLoading(true);
    const { data } = await api.get(`/order-acceptances/${id}`);
    setD(data);
    try {
      const cRes = await api.get(`/cylinders`, { params: {} });
      setLinkedCylinders((cRes.data || []).filter((c) => c.order_acceptance_id === id));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[OaDetail] Failed to load linked cylinders:", err?.response?.status, err?.message);
      setLinkedCylinders([]);
    }
    try {
      const sRes = await api.get(`/supplier-pos`, { params: { order_acceptance_id: id, limit: 50 } });
      setSupplierPos(sRes.data?.items || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[OaDetail] Failed to load supplier POs:", err?.response?.status, err?.message);
      setSupplierPos([]);
    }
    setLoading(false);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  async function exportDoc() {
    const token = localStorage.getItem("hispl_token");
    const res = await fetch(`${API}/order-acceptances/${id}/export-doc`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Export failed"); return; }
    await downloadBlob(res, `OA_${(d.oa_number || "OA").replace(/[^A-Za-z0-9._-]+/g, "_")}.xlsx`);
    toast.success("OA exported");
  }

  async function openReleaseDialog() {
    try {
      const { data } = await api.get(`/order-acceptances/${id}/release-preview`);
      setPreview(data);
      setReleaseDialog(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    }
  }

  async function callAction(path, body, label) {
    setSaving(true);
    try {
      await api.post(`/order-acceptances/${id}/${path}`, body || {});
      toast.success(`${label} success`);
      refresh();
      setHoldDialog(false); setCancelDialog(false); setCloseDialog(false); setDialogReason("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `${label} failed`);
    } finally { setSaving(false); }
  }

  async function release() {
    setSaving(true);
    try {
      const { data } = await api.post(`/order-acceptances/${id}/release`);
      toast.success(`${data.generated_count} cylinder numbers generated (${data.cylinders[0].cylinder_number}–${data.cylinders[data.cylinders.length - 1].cylinder_number})`);
      setReleaseDialog(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Release failed");
    } finally { setSaving(false); }
  }

  if (loading || !d) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;
  }

  const canConfirm = d.status === "Draft" && CONFIRM_ROLES.has(user.role);
  const canRelease = d.status === "Confirmed" && CONFIRM_ROLES.has(user.role);
  const canHold = ["Draft", "Confirmed", "ReleasedToProduction"].includes(d.status) && CONFIRM_ROLES.has(user.role);
  const canResume = d.status === "OnHold" && CONFIRM_ROLES.has(user.role);
  const canCancel = !["Cancelled", "Closed"].includes(d.status) && CONFIRM_ROLES.has(user.role);
  const canClose = d.status !== "Cancelled" && d.status !== "Closed" && CONFIRM_ROLES.has(user.role);
  const totalQty = (d.line_items || []).reduce((s, li) => s + (li.quantity || 0), 0);

  const terms = d.terms || {};
  const termFields = [
    ["Price Basis", terms.price_basis], ["P&F", terms.packing_forwarding],
    ["Payment", terms.payment], ["IGST", terms.igst],
    ["Del. Mode", terms.delivery_mode], ["CGST", terms.cgst],
    ["Inspection", terms.inspection], ["SGST", terms.sgst],
    ["Painting", terms.painting], ["Freight", terms.freight],
    ["Drawing Approval", terms.drawing_approval], ["Insurance", terms.insurance],
  ];

  return (
    <div data-testid="oa-detail-page" className="space-y-5 pb-6">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/order-acceptances" className="hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Order Acceptances
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="font-mono text-slate-900">{d.oa_number}</span>
      </div>

      <Card className="p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">
              Order Acceptance · Final Commercial Stage {d.historical && "· Historical"}
            </div>
            <div className="mt-1 font-mono text-xl md:text-2xl font-bold text-slate-900">{d.oa_number || "—"}</div>
            <div className="mt-2 flex items-center gap-3 text-sm flex-wrap">
              <span className="text-slate-600">
                {d.customer_name || "Customer not set"} · {d.plant_name || "Plant not set"}
              </span>
              {d.department && (<><span className="text-slate-300">·</span><span className="text-slate-600">{d.department}</span></>)}
              {d.project_name && (<><span className="text-slate-300">·</span><span className="text-slate-700 font-medium">{d.project_name}</span></>)}
              <span className="text-slate-300">·</span>
              <span className="text-slate-500 font-mono text-xs">Delivery by: {d.delivery_commitment || "—"}</span>
            </div>
          </div>
          <div className="flex flex-col md:items-end gap-2">
            <div className="flex items-center gap-3">
              <CommercialStatusBadge status={d.status} className="text-xs px-3 py-1" />
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Cylinders</div>
                <div className="font-mono text-lg font-bold text-slate-900">{totalQty} units</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={exportDoc} data-testid="oa-export-doc-btn">
                <FileDown className="w-3.5 h-3.5 mr-1" /> Export OA (Excel)
              </Button>
              {EMAIL_ROLES.has(user.role) && (
                <Button size="sm" variant="outline" onClick={() => setEmailDialog(true)} data-testid="oa-send-email-btn">
                  <Mail className="w-3.5 h-3.5 mr-1" /> Send Email
                </Button>
              )}
              {d.status !== "Cancelled" && CONFIRM_ROLES.has(user.role) && (
                <Button size="sm" variant="outline" onClick={() => setSupplierPoDialog(true)} data-testid="oa-convert-supplier-po-btn"
                        className="border-red-200 text-red-800 hover:bg-red-50">
                  <Truck className="w-3.5 h-3.5 mr-1" /> Convert to Supplier PO
                </Button>
              )}
              {canConfirm && (
                <Button size="sm" data-testid="oa-confirm-btn" onClick={() => callAction("confirm", null, "OA confirmed")} disabled={saving}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirm
                </Button>
              )}
              {canRelease && (
                <Button size="sm" data-testid="oa-release-btn" onClick={openReleaseDialog}
                        className="bg-red-600 hover:bg-red-700 text-white">
                  <Rocket className="w-3.5 h-3.5 mr-1" /> Release
                </Button>
              )}
              {canResume && (
                <Button size="sm" data-testid="oa-resume-btn" onClick={() => callAction("resume", null, "OA resumed")}
                        variant="outline">
                  <PlayCircle className="w-3.5 h-3.5 mr-1" /> Resume
                </Button>
              )}
              {canHold && (
                <Button size="sm" variant="outline" data-testid="oa-hold-btn" onClick={() => setHoldDialog(true)}>
                  <PauseCircle className="w-3.5 h-3.5 mr-1" /> Hold
                </Button>
              )}
              {canClose && (
                <Button size="sm" variant="outline" data-testid="oa-close-btn" onClick={() => setCloseDialog(true)}>
                  <CheckSquare className="w-3.5 h-3.5 mr-1" /> Close
                </Button>
              )}
              {canCancel && (
                <Button size="sm" variant="outline" data-testid="oa-cancel-btn" onClick={() => setCancelDialog(true)}
                        className="text-red-700 border-red-200 hover:bg-red-50">
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
              )}
            </div>
            {d.status === "ReleasedToProduction" && (
              <div className="inline-flex items-center gap-2 text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-3 py-1.5">
                <PackageCheck className="w-3.5 h-3.5" />
                {d.cylinders_generated_count || 0} cylinders generated on {d.released_at?.slice(0, 10)}
              </div>
            )}
            <AdminDeleteButton
              endpoint={`/order-acceptances/${id}`}
              entityName="Order Acceptance"
              entityNumber={d.oa_number}
              redirectAfter="/order-acceptances"
            />
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Commercial Chain</div>
        <CommercialChain type="oa" entity={d} />
      </Card>

      {/* Linked Documents / References — every OA shows this. Missing links must
          never block the workflow; they can be attached later via edit. */}
      <Card className="p-5" data-testid="oa-linked-documents">
        <div className="text-sm font-semibold text-slate-900 mb-3">Linked Documents & References</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <LinkedDocRow
            label="Inquiry"
            value={d.inquiry_number}
            to={d.inquiry_id ? `/inquiries/${d.inquiry_id}` : null}
            testid="linked-inquiry"
          />
          <LinkedDocRow
            label="Quotation"
            value={d.quotation_number}
            extra={d.quotation_date}
            to={d.quotation_id ? `/quotations/${d.quotation_id}` : null}
            testid="linked-quotation"
          />
          <LinkedDocRow
            label="Purchase Order"
            value={d.customer_po_number || d.po_number}
            extra={d.po_date}
            to={d.purchase_order_id ? `/purchase-orders/${d.purchase_order_id}` : null}
            testid="linked-po"
          />
          <LinkedDocRow
            label="Delivery Challan (DC)"
            value={d.delivery_challan_number}
            extra={d.delivery_challan_date}
            to={d.delivery_challan_id ? `/receiving/${d.delivery_challan_id}` : null}
            testid="linked-dc"
            optionalHint="Optional · required only for reconditioning"
          />
        </div>
        <div className="mt-3 text-[11px] text-slate-500">
          Missing links do not block the workflow. Attach documents at any time from the record's own page.
        </div>
      </Card>

      <Card className="p-5">
        <div className="text-sm font-semibold text-slate-900 mb-4">Line Items</div>
        <LineItemsEditor value={d.line_items || []} onChange={() => {}} readOnly showPrice />
      </Card>

      <Card className="p-5" data-testid="oa-terms-card">
        <div className="text-sm font-semibold text-slate-900 mb-3">Terms & Conditions</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          {termFields.map(([k, v]) => (
            <div key={k} className="flex gap-3 py-1 border-b border-slate-100 last:border-0">
              <div className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold w-32 flex-shrink-0">{k}</div>
              <div className="text-slate-900 font-mono">{v || "—"}</div>
            </div>
          ))}
        </div>
      </Card>

      {linkedCylinders.length > 0 && (
        <Card className="p-3">
          <div className="px-2 py-1 flex items-center gap-2">
            <Cylinder className="w-4 h-4 text-slate-500" />
            <div className="text-sm font-semibold text-slate-900">Generated Cylinders · {linkedCylinders.length}</div>
          </div>
          <div className="p-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {linkedCylinders.map((c) => (
              <Link key={c.id} to={`/cylinders/${c.id}`} data-testid={`gen-cyl-${c.cylinder_number}`}
                    className="flex items-center justify-between p-2 border border-slate-200 rounded hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                <div>
                  <div className="font-mono text-sm font-bold text-blue-900">#{c.cylinder_number}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{c.cylinder_status}</div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
              </Link>
            ))}
          </div>
        </Card>
      )}

      {supplierPos.length > 0 && (
        <Card className="p-3" data-testid="oa-linked-supplier-pos">
          <div className="px-2 py-1 flex items-center gap-2">
            <Truck className="w-4 h-4 text-slate-500" />
            <div className="text-sm font-semibold text-slate-900">Sub-Contract Supplier POs · {supplierPos.length}</div>
          </div>
          <div className="p-2 grid grid-cols-1 md:grid-cols-2 gap-2">
            {supplierPos.map((sp) => (
              <Link key={sp.id} to={`/supplier-pos/${sp.id}`} data-testid={`linked-sp-${sp.sp_number}`}
                    className="flex items-start justify-between p-3 border border-slate-200 rounded hover:border-red-300 hover:bg-red-50/30 transition-colors">
                <div>
                  <div className="font-mono text-sm font-bold text-red-900">{sp.sp_number}</div>
                  <div className="text-xs text-slate-700 mt-0.5">{sp.supplier_name} · {sp.scope}</div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    {sp.line_count} lines · Qty {sp.total_qty} · ₹{(sp.total_value || 0).toLocaleString("en-IN")}
                  </div>
                </div>
                <CommercialStatusBadge status={sp.status} className="text-[10px] px-1.5 py-0.5" />
              </Link>
            ))}
          </div>
        </Card>
      )}

      {d.remarks && (
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Remarks</div>
          <p className="text-sm text-slate-700 whitespace-pre-line">{d.remarks}</p>
        </Card>
      )}

      <Card className="p-5">
        <div className="text-sm font-semibold text-slate-900 mb-4">Audit Trail</div>
        <AuditTrail items={d.audit_trail || []} />
      </Card>

      {/* Release dialog */}
      <Dialog open={releaseDialog} onOpenChange={setReleaseDialog}>
        <DialogContent data-testid="release-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-red-600" />Release {d.oa_number} to Production</DialogTitle>
            <DialogDescription>This will generate Cylinder Numbers and lock the Order Acceptance.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="border border-slate-200 rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
                    <tr><th className="px-3 py-2 text-left">Line</th><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2 text-left">Spec</th><th className="px-3 py-2 text-right">Qty</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.lines.map((li) => (
                      <tr key={li.line_no} data-testid={`preview-line-${li.line_no}`}>
                        <td className="px-3 py-2 font-mono">{li.line_no}</td>
                        <td className="px-3 py-2 text-xs">{li.description}<div className="text-[10px] text-slate-500">{li.cylinder_type}</div></td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-700">{li.spec}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold">{li.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-semibold">
                    <tr><td colSpan={3} className="px-3 py-2 text-right text-xs uppercase tracking-wider text-slate-700">Total Cylinders</td>
                      <td className="px-3 py-2 text-right font-mono text-lg font-bold text-red-600" data-testid="preview-total">{preview.total_cylinders_to_generate}</td></tr>
                  </tfoot>
                </table>
              </div>
              {preview.already_generated > 0 && (
                <div className="text-xs bg-red-50 border border-red-200 text-red-900 rounded px-3 py-2">
                  ⚠️ {preview.already_generated} cylinders already exist. Cannot re-release.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseDialog(false)}>Cancel</Button>
            <Button data-testid="release-confirm" onClick={release} disabled={saving || !preview?.can_release}
                    className="bg-red-600 hover:bg-red-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Generate ${preview?.total_cylinders_to_generate || 0} Cylinder Numbers`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hold */}
      <ReasonDialog open={holdDialog} onOpenChange={setHoldDialog} title="Put OA On Hold" reason={dialogReason}
        setReason={setDialogReason} onConfirm={() => callAction("hold", { reason: dialogReason }, "OA on hold")}
        saving={saving} confirmLabel="Hold" />
      {/* Cancel */}
      <ReasonDialog open={cancelDialog} onOpenChange={setCancelDialog} title="Cancel Order Acceptance"
        reason={dialogReason} setReason={setDialogReason} required
        onConfirm={() => callAction("cancel", { reason: dialogReason }, "OA cancelled")}
        saving={saving} confirmLabel="Cancel OA" />
      {/* Close */}
      <ReasonDialog open={closeDialog} onOpenChange={setCloseDialog} title="Close Order Acceptance"
        reason={dialogReason} setReason={setDialogReason}
        onConfirm={() => callAction("close", { reason: dialogReason }, "OA closed")}
        saving={saving} confirmLabel="Close" />

      <ConvertOaToSupplierPoDialog open={supplierPoDialog} onOpenChange={setSupplierPoDialog}
        oa={d} onCreated={() => refresh()} />

      <SendEmailDialog
        open={emailDialog} onClose={() => setEmailDialog(false)}
        defaultSubject={d ? `Order Acceptance ${d.oa_number} — ${d.customer_name}` : ""}
        defaultBody={d ? `Dear Sir/Madam,\n\nPlease find attached Order Acceptance ${d.oa_number} against your PO ${d.customer_po_number || d.po_number}.\n\nBest regards,\nHISPL Sales Team` : ""}
        relatedModule="order_acceptance" relatedRecordId={id}
        relatedRecordNumber={d?.oa_number}
        attachmentEndpoint={`/order-acceptances/${id}/export-doc`}
        attachmentFilename={d ? `OA_${(d.oa_number || "OA").replace(/[^A-Za-z0-9._-]+/g, "_")}.xlsx` : "OA.xlsx"}
      />
    </div>
  );
}

function LinkedDocRow({ label, value, extra, to, testid, optionalHint }) {
  const isLinked = !!value;
  return (
    <div className="flex items-start justify-between gap-3 p-3 rounded border border-slate-200 bg-slate-50/40" data-testid={testid}>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="mt-0.5 font-mono text-sm truncate">
          {isLinked ? (
            to ? (
              <Link to={to} className="text-blue-900 hover:underline">
                {value}{extra && <span className="text-slate-500"> · {extra}</span>}
              </Link>
            ) : (
              <span className="text-slate-900">
                {value}{extra && <span className="text-slate-500"> · {extra}</span>}
              </span>
            )
          ) : (
            <span className="text-slate-400 italic">Not Linked</span>
          )}
        </div>
        {optionalHint && <div className="text-[10px] text-slate-400 mt-1">{optionalHint}</div>}
      </div>
      <span
        className={`text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 border shrink-0 ${
          isLinked
            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
            : "bg-slate-50 text-slate-500 border-slate-200"
        }`}
      >
        {isLinked ? "Linked" : "Missing"}
      </span>
    </div>
  );
}

function ReasonDialog({ open, onOpenChange, title, reason, setReason, onConfirm, saving, confirmLabel, required }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <label className="text-xs uppercase text-slate-500 font-semibold">Reason {required && "*"}</label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Add a note for the audit trail…" data-testid="reason-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Back</Button>
          <Button onClick={onConfirm} disabled={saving || (required && !reason.trim())} data-testid="reason-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
